// Enhanced Dashboard Queries Service
// This service provides comprehensive SQL queries for each dashboard tab

export const enhancedDashboardQueries = {
  
  // Time Trends Tab - Enhanced Performance Trends with Multiple Metrics and Date Ranges
  getTimeTrendsData: (accountId, startDate, endDate, metrics = ['ctr', 'cvr', 'roas', 'cpa', 'spend'], grouping = 'day', adNameFilter = null) => {
    const dateFilter = `adp.date >= '${startDate}' AND adp.date <= '${endDate}'`;
    
    // Build ad name filter if provided
    let adNameFilterClause = '';
    if (adNameFilter && adNameFilter.keywords && adNameFilter.keywords.length > 0) {
      const validKeywords = adNameFilter.keywords.filter(keyword => keyword && keyword.trim());
      if (validKeywords.length > 0) {
        const keywordConditions = validKeywords.map(keyword => 
          `a.name ILIKE '%${keyword.trim()}%'`
        );
        
        if (adNameFilter.logic === 'AND') {
          adNameFilterClause = `AND (${keywordConditions.join(' AND ')})`;
        } else {
          adNameFilterClause = `AND (${keywordConditions.join(' OR ')})`;
        }
      }
    }

    const query = `
      SELECT 
        CASE 
          WHEN '${grouping}' = 'day' THEN adp.date::date
          WHEN '${grouping}' = 'week' THEN DATE_TRUNC('week', adp.date)::date
          WHEN '${grouping}' = 'month' THEN DATE_TRUNC('month', adp.date)::date
          ELSE DATE_TRUNC('day', adp.date)::date
        END as period,
        SUM(adp.impressions) as total_impressions,
        SUM(adp.clicks) as total_clicks,
        SUM(adp.spend) as total_spend,
        SUM(adp.purchases) as total_conversions,
        SUM(adp.purchase_value) as total_revenue,
        ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
        ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
        ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
        ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.purchases), 0))::numeric, 2) as cpa,
        ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
        COUNT(DISTINCT adp.raw_ad_id) as active_ads
      FROM t_ad_daily_performance adp
      JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id
      WHERE adp.raw_account_id = '${accountId}' 
        AND ${dateFilter}
        ${adNameFilterClause}
      GROUP BY 
        CASE 
          WHEN '${grouping}' = 'day' THEN adp.date::date
          WHEN '${grouping}' = 'week' THEN DATE_TRUNC('week', adp.date)::date
          WHEN '${grouping}' = 'month' THEN DATE_TRUNC('month', adp.date)::date
          ELSE DATE_TRUNC('day', adp.date)::date
        END 
      ORDER BY period ASC`;

    return query;
  },

  // Creative Breakdown Tab - Top Performing Ads in Multiple Dimensions
  getCreativeBreakdownData: (accountId, startDate, endDate, dimension = 'roas', limit = 20, adNameFilter = null) => {
    const dateFilter = `adp.date >= '${startDate}' AND adp.date <= '${endDate}'`;
    
    // Build ad name filter if provided
    let adNameFilterClause = '';
    if (adNameFilter && adNameFilter.keywords && adNameFilter.keywords.length > 0) {
      const validKeywords = adNameFilter.keywords.filter(keyword => keyword && keyword.trim());
      if (validKeywords.length > 0) {
        const keywordConditions = validKeywords.map(keyword => 
          `a.name ILIKE '%${keyword.trim()}%'`
        );
        
        if (adNameFilter.logic === 'AND') {
          adNameFilterClause = `AND (${keywordConditions.join(' AND ')})`;
        } else {
          adNameFilterClause = `AND (${keywordConditions.join(' OR ')})`;
        }
      }
    }

    // Define sorting based on dimension
    let orderByClause = '';
    switch (dimension) {
      case 'roas':
        orderByClause = 'ORDER BY roas DESC';
        break;
      case 'spend':
        orderByClause = 'ORDER BY total_spend DESC';
        break;
      case 'impressions':
        orderByClause = 'ORDER BY total_impressions DESC';
        break;
      case 'ctr':
        orderByClause = 'ORDER BY ctr DESC';
        break;
      case 'cvr':
        orderByClause = 'ORDER BY cvr DESC';
        break;
      case 'fatigue':
        orderByClause = 'ORDER BY fatigue_score DESC';
        break;
      default:
        orderByClause = 'ORDER BY roas DESC';
    }

    const query = `
      SELECT 
        a.raw_ad_id,
        a.name as ad_name,
        a.asset_id,
        COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown') as ad_type,
        COALESCE(il.f_product_position, vl.cf_product_position) as product_position,
        COALESCE(il.f_setting, vl.cf_setting) as setting,
        COALESCE(il.f_lighting, vl.cf_lighting) as lighting,
        vl.cf_voice_tone as voice_tone,
        vl.cf_music_mood as music_mood,
        COALESCE(il.f_text_alignment, vl.cf_text_alignment) as text_alignment,
        COALESCE(il.f_text_position, vl.cf_text_position) as text_position,
        COALESCE(il.f_text_size, vl.cf_text_size) as text_size,
        SUM(adp.impressions) as total_impressions,
        SUM(adp.clicks) as total_clicks,
        SUM(adp.spend) as total_spend,
        SUM(adp.purchases) as total_conversions,
        SUM(adp.purchase_value) as total_revenue,
        ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
        ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
        ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
        ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.purchases), 0))::numeric, 2) as cpa,
        ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
        -- Fatigue score calculation (higher spend with declining performance = higher fatigue)
        CASE 
          WHEN SUM(adp.impressions) >= 1000 THEN 
            ROUND((SUM(adp.spend) * (1 - (SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0)))::numeric, 2)
          ELSE 0 
        END as fatigue_score,
        COUNT(DISTINCT adp.date) as active_days,
        MIN(adp.date) as first_seen,
        MAX(adp.date) as last_seen
      FROM t_ad_daily_performance adp
      JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id
      LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
      LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
      WHERE adp.raw_account_id = '${accountId}' 
        AND ${dateFilter}
        ${adNameFilterClause}
      GROUP BY 
        a.raw_ad_id, a.name, a.asset_id,
        COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown'),
        COALESCE(il.f_product_position, vl.cf_product_position),
        COALESCE(il.f_setting, vl.cf_setting),
        COALESCE(il.f_lighting, vl.cf_lighting),
        vl.cf_voice_tone,
        vl.cf_music_mood,
        COALESCE(il.f_text_alignment, vl.cf_text_alignment),
        COALESCE(il.f_text_position, vl.cf_text_position),
        COALESCE(il.f_text_size, vl.cf_text_size)
      HAVING SUM(adp.impressions) >= 1000
      ${orderByClause}
      LIMIT ${limit}`;

    return query;
  },

  // Creative Attributes Tab - Granular Feature Analysis
  getCreativeAttributesData: (accountId, startDate, endDate, featureCategory = 'all', adNameFilter = null) => {
    const dateFilter = `adp.date >= '${startDate}' AND adp.date <= '${endDate}'`;
    
    // Build ad name filter if provided
    let adNameFilterClause = '';
    if (adNameFilter && adNameFilter.keywords && adNameFilter.keywords.length > 0) {
      const validKeywords = adNameFilter.keywords.filter(keyword => keyword && keyword.trim());
      if (validKeywords.length > 0) {
        const keywordConditions = validKeywords.map(keyword => 
          `a.name ILIKE '%${keyword.trim()}%'`
        );
        
        if (adNameFilter.logic === 'AND') {
          adNameFilterClause = `AND (${keywordConditions.join(' AND ')})`;
        } else {
          adNameFilterClause = `AND (${keywordConditions.join(' OR ')})`;
        }
      }
    }

    // Define feature categories
    const featureCategories = {
      visual: [
        'Visual: Product Position', 'Visual: Setting', 'Visual: Lighting', 
        'Visual: Color Scheme', 'Visual: Composition', 'Visual: Brand Presence'
      ],
      audio: [
        'Audio: Voice Tone', 'Audio: Music Mood', 'Audio: Speaking Pace',
        'Audio: Background Music', 'Audio: Sound Effects', 'Audio: Voice Gender'
      ],
      text: [
        'Text: Alignment', 'Text: Position', 'Text: Size',
        'Text: Font Style', 'Text: Color', 'Text: Language'
      ],
      creator: [
        'Creator: Influencer Type', 'Creator: Audience Match', 'Creator: Content Style',
        'Creator: Authenticity', 'Creator: Engagement Rate'
      ],
      music: [
        'Music: Genre', 'Music: Tempo', 'Music: Mood',
        'Music: Lyrics', 'Music: Instrumentation'
      ]
    };

    // Select features based on category
    let selectedFeatures = [];
    let selectedFeatureValues = [];
    
    if (featureCategory === 'all') {
      selectedFeatures = [
        ...featureCategories.visual,
        ...featureCategories.audio,
        ...featureCategories.text,
        ...featureCategories.creator,
        ...featureCategories.music
      ];
      selectedFeatureValues = [
        'COALESCE(il.f_product_position, vl.cf_product_position)',
        'COALESCE(il.f_setting, vl.cf_setting)',
        'COALESCE(il.f_lighting, vl.cf_lighting)',
        'COALESCE(il.f_color_scheme, vl.cf_color_scheme)',
        'COALESCE(il.f_composition, vl.cf_composition)',
        'COALESCE(il.f_brand_presence, vl.cf_brand_presence)',
        'vl.cf_voice_tone',
        'vl.cf_music_mood',
        'vl.cf_speaking_pace',
        'vl.cf_background_music',
        'vl.cf_sound_effects',
        'vl.cf_voice_gender',
        'COALESCE(il.f_text_alignment, vl.cf_text_alignment)',
        'COALESCE(il.f_text_position, vl.cf_text_position)',
        'COALESCE(il.f_text_size, vl.cf_text_size)',
        'COALESCE(il.f_font_style, vl.cf_font_style)',
        'COALESCE(il.f_text_color, vl.cf_text_color)',
        'COALESCE(il.f_language, vl.cf_language)',
        'vl.cf_influencer_type',
        'vl.cf_audience_match',
        'vl.cf_content_style',
        'vl.cf_authenticity',
        'vl.cf_engagement_rate',
        'vl.cf_music_genre',
        'vl.cf_music_tempo',
        'vl.cf_music_mood',
        'vl.cf_music_lyrics',
        'vl.cf_music_instrumentation'
      ];
    } else if (featureCategories[featureCategory]) {
      selectedFeatures = featureCategories[featureCategory];
      // Map to corresponding feature values (simplified for this example)
      selectedFeatureValues = selectedFeatures.map(feature => {
        const featureKey = feature.split(': ')[1].toLowerCase().replace(/\s+/g, '_');
        return `COALESCE(il.f_${featureKey}, vl.cf_${featureKey})`;
      });
    }

    // Build the CASE statement for feature categories
    const caseStatements = [];
    Object.entries(featureCategories).forEach(([category, features]) => {
      features.forEach(feature => {
        caseStatements.push(`WHEN '${feature}' THEN '${category}'`);
      });
    });
    const caseStatement = caseStatements.join('\n            ');

    const query = `
      SELECT 
        feature_name,
        feature_category,
        feature_value,
        COUNT(DISTINCT raw_ad_id) as number_of_ads,
        SUM(spend) as total_spend,
        SUM(impressions) as total_impressions,
        SUM(clicks) as total_clicks,
        SUM(purchases) as total_conversions,
        SUM(purchase_value) as total_revenue,
        ROUND((SUM(clicks)::float / NULLIF(SUM(impressions), 0) * 100)::numeric, 4) as ctr,
        ROUND((SUM(purchases)::float / NULLIF(SUM(clicks), 0) * 100)::numeric, 4) as cvr,
        ROUND((SUM(purchase_value)::float / NULLIF(SUM(spend), 0))::numeric, 2) as roas,
        ROUND((SUM(spend)::float / NULLIF(SUM(purchases), 0))::numeric, 2) as cpa,
        ROUND((SUM(spend)::float / NULLIF(SUM(impressions), 0) * 1000)::numeric, 2) as cpm,
        ROUND((SUM(purchase_value)::float / NULLIF(SUM(impressions), 0) * 1000)::numeric, 2) as rpm
      FROM (
        SELECT DISTINCT
          a.raw_ad_id,
          unnest(ARRAY[${selectedFeatures.map(f => `'${f}'`).join(', ')}]) as feature_name,
          CASE 
            ${caseStatement}
          END as feature_category,
          unnest(ARRAY[${selectedFeatureValues.join(', ')}]) as feature_value,
          adp.spend,
          adp.impressions,
          adp.clicks,
          adp.purchases,
          adp.purchase_value
        FROM t_ad a
        LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
        LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
        JOIN (
          SELECT raw_ad_id, 
                 SUM(spend) as spend,
                 SUM(impressions) as impressions,
                 SUM(clicks) as clicks,
                 SUM(purchases) as purchases,
                 SUM(purchase_value) as purchase_value
          FROM t_ad_daily_performance
          WHERE raw_account_id = '${accountId}' AND ${dateFilter}
          GROUP BY raw_ad_id
        ) adp ON a.raw_ad_id = adp.raw_ad_id
        WHERE a.raw_account_id = '${accountId}' AND adp.spend > 0
          ${adNameFilterClause}
      ) features
      WHERE feature_value IS NOT NULL AND feature_value != '' AND feature_value != 'Unknown'
      GROUP BY feature_name, feature_category, feature_value
      HAVING SUM(impressions) >= 1000 AND COUNT(DISTINCT raw_ad_id) >= 2
      ORDER BY total_spend DESC
      LIMIT 50`;

    return query;
  },

  // Audience Creative Tab - Audience Response Analysis (placeholder for future enhancement)
  getAudienceCreativeData: (accountId, startDate, endDate, adNameFilter = null) => {
    // This would require additional audience data tables
    // For now, return a basic query structure
    return `
      SELECT 
        'Audience analysis requires additional data tables' as message,
        'Coming Soon' as status`;
  },

  // Platform Placement Tab - Channel Optimization (placeholder for future enhancement)
  getPlatformPlacementData: (accountId, startDate, endDate, adNameFilter = null) => {
    // This would require platform/placement data
    // For now, return a basic query structure
    return `
      SELECT 
        'Platform analysis requires additional data tables' as message,
        'Coming Soon' as status`;
  },

  // Testing Experiments Tab - A/B Test Results (placeholder for future enhancement)
  getTestingExperimentsData: (accountId, startDate, endDate, adNameFilter = null) => {
    // This would require A/B testing data
    // For now, return a basic query structure
    return `
      SELECT 
        'A/B testing analysis requires additional data tables' as message,
        'Coming Soon' as status`;
  }
};

export default enhancedDashboardQueries;
